import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown, MessageCircle } from "lucide-react";
import { CONTATO } from "../data/chales";

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  return (
    <section ref={ref} id="topo" className="relative min-h-screen overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y }} className="absolute inset-0 scale-110">
        <img
          src="/fotos/superluxo/1.webp"
          alt="Chalé do Laguna Alto Viçosa entre as montanhas"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#0f1f17]/90 via-[#0f1f17]/30 to-[#0f1f17]/40" />
      <div className="relative z-10 flex min-h-screen flex-col justify-end px-6 pb-24 md:px-12 lg:px-24">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="mb-6 text-xs font-bold uppercase tracking-[0.3em] text-[#d8c9b0]"
        >
          Alto Caxixe — Venda Nova do Imigrante, Espírito Santo
        </motion.p>
        <h1
          className="text-5xl font-bold uppercase leading-[1.05] tracking-tighter text-[#F6F5F2] md:text-7xl"
          data-testid="hero-title"
        >
          <Line delay={0.3}>Laguna</Line>
          <Line delay={0.45}>Alto Viçosa</Line>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.8 }}
          className="mt-6 max-w-xl text-base leading-relaxed text-[#F6F5F2]/85 md:text-lg"
        >
          Doze chalés de luxo entre montanhas, águas e silêncio. Hidromassagem, lareira, café da
          manhã no chalé e o aconchego que só as montanhas capixabas têm.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.8 }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <a
            href="#chales"
            className="bg-[#8C5A40] px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-[#F6F5F2] transition-colors hover:bg-[#704833]"
            data-testid="hero-cta-chales"
          >
            Conhecer os chalés
          </a>
          <a
            href={`https://wa.me/${CONTATO.whatsapp}`}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 border border-[#F6F5F2]/60 px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-[#F6F5F2] transition-colors hover:bg-[#F6F5F2] hover:text-[#1C3026]"
            data-testid="hero-cta-whatsapp"
          >
            <MessageCircle className="h-4 w-4" /> Falar no WhatsApp
          </a>
        </motion.div>
      </div>
      <motion.div
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
        className="absolute bottom-6 right-6 z-10 text-[#F6F5F2]/70"
      >
        <ChevronDown className="h-6 w-6" />
      </motion.div>
    </section>
  );
}
